import React from 'react';
import { Globe, ExternalLink } from 'lucide-react';

interface AdminSavedLinksProps {
  location: any;
}

const PLATFORMS = [
  { key: 'googleBusiness', label: 'Google Business', color: 'bg-blue-500', idField: 'googleBusinessId', urlField: 'googleBusinessUrl' },
  { key: 'facebook', label: 'Facebook', color: 'bg-blue-600', idField: 'facebookPageId', urlField: 'facebookUrl' },
  { key: 'instagram', label: 'Instagram', color: 'bg-pink-500', idField: 'instagramHandle', urlField: 'instagramUrl' },
  { key: 'yelp', label: 'Yelp', color: 'bg-red-500', idField: 'yelpId', urlField: 'yelpUrl' },
  { key: 'tripAdvisor', label: 'TripAdvisor', color: 'bg-green-600', idField: 'tripAdvisorId', urlField: 'tripAdvisorUrl' },
  { key: 'website', label: 'Website', color: 'bg-stone-600', idField: null, urlField: 'websiteUrl' },
];

const AdminSavedLinks: React.FC<AdminSavedLinksProps> = ({ location }) => {
  const online = location?.onlinePresence || location;
  const hasPlatforms = PLATFORMS.some(p => {
    const id = p.idField ? online?.[p.idField] : null;
    const url = online?.[p.urlField];
    return id || url;
  });

  if (!hasPlatforms) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <div className="text-center py-8 text-stone-400">
          <Globe size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No online presence configured</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {PLATFORMS.map(platform => {
        const id = platform.idField ? online?.[platform.idField] : null;
        const url = online?.[platform.urlField];
        if (!id && !url) return null;

        return (
          <div key={platform.key} className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 ${platform.color} rounded-lg flex items-center justify-center shrink-0`}>
                <Globe size={16} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-stone-800">{platform.label}</h4>
                {id && (
                  <p className="text-xs text-stone-500 mt-0.5">
                    ID: <span className="font-mono text-stone-600">{id}</span>
                  </p>
                )}
                {url && (
                  <a
                    href={url.startsWith('http') ? url : `https://${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-1"
                  >
                    {url}
                    <ExternalLink size={10} />
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AdminSavedLinks;
